<template>
    <div class="layout"
         v-loading="requestInProgress"
         element-loading-text="Loading"
         element-loading-background="rgba(0, 0, 0, 0.8)"
         element-loading-custom-class="layout-loading-mask">
        <header class="sticky-top fixed-top w-100">
            <app-header />
        </header>
        <main class="size-constraint">
            <slot v-if="appIsInitialized" />
            <div class="fill" v-else>
                <div class="h-100 d-flex flex-column justify-content-center align-items-center">
                    <el-progress type="dashboard" :percentage="percentage">
                        <template #default="{ percentage }">
                            <div class="font-large-5 mb-1">{{ percentage }}%</div>
                            <div>Initializing</div>
                        </template>
                    </el-progress>
                </div>
            </div>
        </main>
        <footer>
            <!-- Footer -->
        </footer>
    </div>
</template>

<script lang="ts">
import { defineComponent, PropType } from "vue";
import AppHeader from "../components/layout/AppHeader.vue";
import {rootContext} from "@/store";
import {DEFAULT_REQUIRED_MODULES, ModuleKey} from "@/store/modules";

export default defineComponent({
    name: "DefaultLayout",
    components: {AppHeader},
    props: {
        onUpdate: {
            type: Function,
            required: false
        },
        requestInProgress: {
            type: Boolean,
            required: false,
            default: false
        },
        requiredModules: {
            type: Array as PropType<ModuleKey[]>,
            required: false,
            default: () => []
        }
    },
    mounted(): void {
        this.fillToViewHeight();
        this.startProgressAnimation();
    },
    beforeUnmount(): void {
        this.stopProgressAnimation();
    },
    updated(): void {
        this.fillToViewHeight();

        this.$events.emit("update");
    },
    data() {
        return {
            displayedPercentage: 0 as number,
            progressTimer: null as number | null
        };
    },
    computed: {
        effectiveRequiredModules(): ModuleKey[] {
            return Array.from(
                new Set([
                    ...DEFAULT_REQUIRED_MODULES,
                    ...this.requiredModules
                ])
            );
        },
        moduleInitializationStates(): boolean[] {
            const modules = rootContext.modules;

            return this.effectiveRequiredModules.map(moduleKey => {
                return modules[moduleKey]?.state?.isInitialized === true;
            });
        },
        appIsInitialized(): boolean {
            return this.moduleInitializationStates.every(Boolean);
        },
        realPercentage(): number {
            if (this.moduleInitializationStates.length === 0) {
                return 100;
            }

            const initializedCount = this.moduleInitializationStates.filter(Boolean).length;

            return Math.round(
                (initializedCount / this.moduleInitializationStates.length) * 100
            );
        },
        percentage(): number {
            return this.displayedPercentage;
        }
    },
    watch: {
        realPercentage: {
            immediate: true,
            handler(newValue: number) {
                this.syncDisplayedPercentage(newValue);
            }
        }
    },
    methods: {
        fillToViewHeight(): void {
            for (const el of document.querySelectorAll(".fill")) {
                if (!(el instanceof HTMLElement)) continue;

                const rect = el.getBoundingClientRect();

                const height = window.innerHeight - rect.top;

                if (rect.height > height) return;

                el.style.height = Math.round(height).toString() + "px";
            }
        },
        startProgressAnimation(): void {
            this.stopProgressAnimation();

            this.progressTimer = window.setInterval(() => {
                if (this.appIsInitialized) {
                    if (this.displayedPercentage < 100) {
                        this.displayedPercentage += 1;
                    }
                    return;
                }

                const cap = Math.min(95, this.realPercentage + (100 / this.effectiveRequiredModules.length) - 5);
                const remaining = cap - this.displayedPercentage;

                if (remaining <= 0) return;

                if (remaining > 10) {
                    this.displayedPercentage += 2;
                } else {
                    this.displayedPercentage += 1;
                }
            }, 600);
        },

        stopProgressAnimation(): void {
            if (this.progressTimer !== null) {
                clearInterval(this.progressTimer);
                this.progressTimer = null;
            }
        },

        syncDisplayedPercentage(realPercentage: number): void {
            if (realPercentage >= 100) {
                this.displayedPercentage = 100;
                return;
            }

            if (this.displayedPercentage < realPercentage) {
                this.displayedPercentage = realPercentage;
            }
        }
    }
});
</script>

<style lang="scss" scoped>
.layout {
    min-height: 100vh;

    .loading-label {
      font-size: 1rem;
    }

    .loading-icon {
      font-size: 1.1rem;
    }

    .is-spinning {
      animation: rotating 1s linear infinite;
    }

    @keyframes rotating {
      0% {
        transform: rotate(0deg);
      }
      100% {
        transform: rotate(360deg);
      }
    }

    :deep(.layout-loading-mask) {
        inset: 0;
        position: fixed;
    }

    :deep(.layout-loading-mask .el-loading-spinner) {
        .circular {
            height: 3rem;
            width: 3rem;
        }

        .path {
            stroke: #ffffff;
        }

        .el-loading-text {
            color: #ffffff;
            font-size: 1.2em;
        }
    }
}
</style>
